import { useCallback, useEffect, useRef, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Wand2, Upload, FileCheck2, FileWarning, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SectionCard, Field, Skeleton } from './ui';

const MODES = ['solve', 'review', 'generate'];
const SEVERITY = ['low', 'medium', 'high', 'critical'];
const PROBLEM_TYPES = ['process', 'technical', 'compliance', 'market', 'financial', 'other'];

function LaneLabel({ label }) {
  const { t } = useTranslation();
  const map = {
    'corpus-grounded-law': { txt: t('pathB.laneLaw'), cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    precedent: { txt: t('pathB.lanePrecedent'), cls: 'bg-sky-50 text-sky-700 border-sky-200' },
    'submitted-document': { txt: t('pathB.laneDoc'), cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    'modeled-not-fact': { txt: t('pathB.laneModeled'), cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  };
  const m = map[label] || map['modeled-not-fact'];
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`} data-testid={`lane-label-${label}`}>{m.txt}</span>;
}

const STATUS_CLS = {
  adequate: 'bg-emerald-100 text-emerald-800',
  weak: 'bg-amber-100 text-amber-800',
  gap: 'bg-orange-100 text-orange-800',
  conflict: 'bg-red-100 text-red-800',
  novel: 'bg-indigo-100 text-indigo-800',
  'already-implemented': 'bg-muted text-foreground',
  'previously-rejected': 'bg-rose-100 text-rose-800',
};

function StatusBadge({ status }) {
  return <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold ${STATUS_CLS[status] || 'bg-muted text-foreground'}`} data-testid={`status-${status}`}>{status}</span>;
}

function BriefList({ title, items, testId }) {
  return (
    <SectionCard title={title} testId={testId}>
      <ul className="space-y-3">
        {items.map((s, i) => (
          <li key={`${s.title}-${i}`} className="border border-border rounded-md p-3">
            <div className="flex items-center justify-between gap-2 mb-1"><span className="font-medium text-foreground">{s.title}</span><LaneLabel label={s.label} /></div>
            <p className="text-sm text-muted-foreground">{s.detail}</p>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function AttentionNotice({ title, testId }) {
  const { t } = useTranslation();
  return (
    <SectionCard title={title} testId={testId}>
      <p className="text-sm text-amber-700 flex items-center gap-2" data-testid={`${testId}-attention`}>
        <AlertTriangle className="h-4 w-4" /> {t('pathB.needsReviewerAttention')}
      </p>
    </SectionCard>
  );
}

function ReportView({ report }) {
  const { t } = useTranslation();
  const findings = report.dimension_findings || [];
  const frame = report.verdict_frame || {};
  const flags = report.meta?.flags_summary || {};
  return (
    <div className="space-y-6" data-testid="solution-report">
      <SectionCard title={t('pathB.reportTitle')} testId="report-subject-card">
        <p className="text-foreground leading-relaxed">{report.problem_or_subject}</p>
        {Object.keys(flags).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="report-flags-summary">
            {Object.entries(flags).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5"><StatusBadge status={k} /><span className="text-xs text-muted-foreground">×{v}</span></span>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title={t('pathB.dimensionFindings')} testId="report-dimensions-card">
        {findings.length === 0 ? (
          <p className="text-sm text-amber-700" data-testid="report-no-dimensions"><T k="pathB.noDimensions" /></p>
        ) : (
          <ul className="space-y-3">
            {findings.map((f, i) => (
              <li key={`${f.dimension_key}-${i}`} className="border border-border rounded-md p-3" data-testid={`report-dim-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <span className="font-medium text-foreground">{f.title}</span>
                  <div className="flex items-center gap-2"><StatusBadge status={f.status} /><LaneLabel label={f.label} /></div>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{f.evidence}</p>
                {(f.lanes_used || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-xs text-muted-foreground">{t('pathB.lanesUsed')}:</span>
                    {f.lanes_used.map((l) => <LaneLabel key={l} label={l} />)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {report.sub_mode === 'solve' && ((report.candidate_solutions || []).length > 0 ? (
        <BriefList title={t('pathB.candidateSolutions')} items={report.candidate_solutions} testId="report-solutions-card" />
      ) : report.attention?.candidate_solutions ? (
        <AttentionNotice title={t('pathB.candidateSolutions')} testId="report-solutions-card" />
      ) : null)}
      {report.sub_mode === 'generate' && ((report.idea_briefs || []).length > 0 ? (
        <BriefList title={t('pathB.ideaBriefs')} items={report.idea_briefs} testId="report-ideas-card" />
      ) : report.attention?.idea_briefs ? (
        <AttentionNotice title={t('pathB.ideaBriefs')} testId="report-ideas-card" />
      ) : null)}

      {(report.limitations || []).length > 0 ? (
        <SectionCard title={t('pathB.limitations')} testId="report-limitations-card">
          <ul className="list-disc ps-5 space-y-1 text-sm text-muted-foreground">
            {report.limitations.map((l, i) => <li key={`lim-${i}-${l.slice(0, 12)}`}>{l}</li>)}
          </ul>
        </SectionCard>
      ) : report.attention?.limitations ? (
        <AttentionNotice title={t('pathB.limitations')} testId="report-limitations-card" />
      ) : null}

      <SectionCard title={t('pathB.verdictFrame')} testId="report-verdict-frame-card">
        <p className="text-xs text-muted-foreground mb-3"><T k="pathB.reviewerCompletes" /></p>
        {frame.decision_prompt && <p className="text-foreground font-medium mb-3">{frame.decision_prompt}</p>}
        {(frame.options || []).length > 0 && (
          <div className="mb-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1"><T k="pathB.options" /></p>
            <div className="flex flex-wrap gap-2">{frame.options.map((o, i) => <span key={`opt-${i}-${o.slice(0, 12)}`} className="px-3 py-1 rounded-full border border-border text-sm text-muted-foreground">{o}</span>)}</div>
          </div>
        )}
        {(frame.conditions_to_consider || []).length > 0 && (
          <div className="mb-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1"><T k="pathB.conditionsConsider" /></p>
            <ul className="list-disc ps-5 space-y-1 text-sm text-muted-foreground">{frame.conditions_to_consider.map((c, i) => <li key={`cond-${i}-${c.slice(0, 12)}`}>{c}</li>)}</ul>
          </div>
        )}
        {(frame.open_questions || []).length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1"><T k="pathB.openQuestions" /></p>
            <ul className="list-disc ps-5 space-y-1 text-sm text-muted-foreground">{frame.open_questions.map((q, i) => <li key={`oq-${i}-${q.slice(0, 12)}`}>{q}</li>)}</ul>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

export default function PathBStage({ projectId, userId, onChanged }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [mode, setMode] = useState('solve');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState({ problem_type: '', domains: '', jurisdiction: '', stakeholders: '', constraints: '', definition_of_solved: '', severity: '' });
  const [showOptional, setShowOptional] = useState(false);
  const [doc, setDoc] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.out('get_solution_report', { project_id: projectId }); setReport(r.solution_report); }
    catch (e) { console.debug('No solution report yet:', e?.message); }
    finally { setLoaded(true); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await api.uploadPathbDoc(file, projectId, userId);
      const res = await api.pathbIngest(projectId, path, file.name, file.type);
      setDoc(res);
      setPasteText('');
      if (res.status !== 'parsed') toast.warning(t('pathB.docStatusBinary'));
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const hasReadableDoc = (doc && doc.status === 'parsed') || pasteText.trim().length > 0;
  const canGenerate = description.trim().length > 0 && (mode !== 'review' || hasReadableDoc);

  const run = async () => {
    setBusy(true); setReport(null); setProgress(null);
    try {
      const params = { project_id: projectId, sub_mode: mode, description: description.trim(), fields };
      if (pasteText.trim()) params.document_text = pasteText.trim();
      else if (doc?.document_id && doc.status === 'parsed') params.document_id = doc.document_id;

      const plan = await api.generateSolutionReport(params);
      if (plan.done) {
        setReport(plan.solution_report);
      } else {
        const dims = plan.plan.dimensions || [];
        const total = plan.plan.total;
        let final = null;
        for (let i = 0; i < total; i += 1) {
          setProgress({ done: i, total, title: dims[i]?.title });
          const r = await api.generateSolutionReportStep(projectId, i);
          if (r.done) final = r.solution_report;
        }
        setProgress({ done: total, total });
        setReport(final);
      }
      onChanged?.();
      toast.success(t('pathB.reportTitle'));
    } catch (err) {
      toast.error(err.body?.error || err.message);
    } finally { setBusy(false); setProgress(null); }
  };

  return (
    <div className="space-y-6" data-testid="pathb-stage">
      <SectionCard title={t('pathB.title')} testId="pathb-intake-card">
        <p className="text-sm text-muted-foreground -mt-2"><T k="pathB.subtitle" /></p>

        <div className="flex gap-2" data-testid="pathb-mode-switch">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              data-testid={`pathb-mode-${m}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${mode === m ? 'bg-slate-900 text-white border-slate-900' : 'bg-card text-muted-foreground border-border hover:border-slate-400'}`}
            >
              {t(`pathB.mode${m.charAt(0).toUpperCase()}${m.slice(1)}`)}
            </button>
          ))}
        </div>

        <Field label={<T k="pathB.description" />}>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('pathB.descriptionPlaceholder')} className="min-h-[100px]" data-testid="pathb-description" />
        </Field>

        <button type="button" onClick={() => setShowOptional((v) => !v)} className="text-sm text-primary underline" data-testid="pathb-toggle-optional">
          {t('pathB.optionalFields')} {showOptional ? '▲' : '▼'}
        </button>
        {showOptional && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="pathb-optional-fields">
            <Field label={<T k="pathB.problemType" />}>
              <Select value={fields.problem_type || 'none'} onValueChange={(v) => setFields((f) => ({ ...f, problem_type: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="pathb-problem-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {PROBLEM_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={<T k="pathB.severity" />}>
              <Select value={fields.severity || 'none'} onValueChange={(v) => setFields((f) => ({ ...f, severity: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="pathb-severity"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {SEVERITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={<T k="pathB.domains" />}><Input value={fields.domains} onChange={(e) => setFields((f) => ({ ...f, domains: e.target.value }))} placeholder={t('pathB.domainsPlaceholder')} data-testid="pathb-domains" /></Field>
            <Field label={<T k="pathB.jurisdiction" />}><Input value={fields.jurisdiction} onChange={(e) => setFields((f) => ({ ...f, jurisdiction: e.target.value }))} data-testid="pathb-jurisdiction" /></Field>
            <Field label={<T k="pathB.stakeholders" />}><Input value={fields.stakeholders} onChange={(e) => setFields((f) => ({ ...f, stakeholders: e.target.value }))} data-testid="pathb-stakeholders" /></Field>
            <Field label={<T k="pathB.definitionSolved" />}><Input value={fields.definition_of_solved} onChange={(e) => setFields((f) => ({ ...f, definition_of_solved: e.target.value }))} data-testid="pathb-defsolved" /></Field>
            <div className="sm:col-span-2"><Field label={<T k="pathB.constraints" />}><Textarea value={fields.constraints} onChange={(e) => setFields((f) => ({ ...f, constraints: e.target.value }))} className="min-h-[60px]" data-testid="pathb-constraints" /></Field></div>
          </div>
        )}

        <Field label={<T k="pathB.document" />}>
          <p className="text-xs text-muted-foreground mb-2"><T k="pathB.uploadHint" /></p>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".txt,.md,.csv,.json,.tsv,.log,text/*,application/json" onChange={onUpload} className="hidden" data-testid="pathb-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pathb-upload-button">
              <Upload className="h-4 w-4 me-2" /> {uploading ? t('pathB.uploading') : t('pathB.uploadButton')}
            </Button>
            {doc && (
              <span className="inline-flex items-center gap-1.5 text-sm" data-testid="pathb-doc-status">
                {doc.status === 'parsed' ? <FileCheck2 className="h-4 w-4 text-emerald-600" /> : <FileWarning className="h-4 w-4 text-amber-600" />}
                {doc.status === 'parsed' ? t('pathB.docStatusParsed') : t('pathB.docStatusBinary')}
              </span>
            )}
          </div>
          <div className="mt-3">
            <p className="text-xs text-muted-foreground mb-1"><T k="pathB.pasteText" /></p>
            <Textarea value={pasteText} onChange={(e) => { setPasteText(e.target.value); if (e.target.value.trim()) setDoc(null); }} placeholder={t('pathB.pastePlaceholder')} className="min-h-[80px] font-data" data-testid="pathb-paste-text" />
          </div>
        </Field>

        {mode === 'review' && !hasReadableDoc && (
          <p className="text-sm text-amber-700" data-testid="pathb-review-hint"><T k="pathB.reviewNeedsDoc" /></p>
        )}

        <div>
          <Button onClick={run} disabled={!canGenerate || busy} data-testid="pathb-generate-button">
            <Wand2 className="h-4 w-4 me-2" /> {busy ? t('pathB.generating') : t('pathB.generate')}
          </Button>
          {busy && progress && (
            <div className="mt-3" data-testid="pathb-progress">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{progress.title || ''}</span>
                <span>{progress.done}/{progress.total}</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {!loaded ? <Skeleton className="h-40 w-full" /> : (report && <ReportView report={report} />)}
    </div>
  );
}
